<template>
  <div class="p-6">
    <div v-if="loading" class="flex justify-center py-8">
      <el-skeleton :rows="5" animated />
    </div>

    <el-alert
      v-else-if="error"
      type="error"
      :title="i18n.t('errorLoadingData')"
      :description="error.message"
      show-icon
      :closable="false">
      <el-button @click="retryFetch">
        {{ i18n.t('retry') }}
      </el-button>
    </el-alert>

    <div
      v-else-if="!personStats"
      class="flex flex-col items-center justify-center py-8">
      <EmptyState
        :icon="Clock"
        :title="i18n.t('browserExtension.noActivityDataTitle')"
        :text="i18n.t('browserExtension.noActivityDataDescription')"
        :show-docs-btn="false" />
    </div>

    <div v-else class="space-y-8">
      <!-- Summary Statistics (Moved to top) -->
      <div v-if="personStats">
        <h3 class="mb-4 text-base font-semibold text-gray-900 dark:text-white">
          {{ i18n.t('browserExtension.activitySummary') }}
        </h3>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <KpiCard
            :title="i18n.t('browserExtension.totalSessions')"
            :value="personStats.metadata.total_sessions"
            :icon="Activity" />
          <KpiCard
            :title="i18n.t('browserExtension.activeDays')"
            :value="personStats.metadata.active_days"
            :icon="Calendar" />
        </div>
      </div>

      <!-- Activity Timeline -->
      <div v-if="hasActivityData">
        <h3 class="mb-6 text-base font-semibold text-gray-900 dark:text-white">
          {{ i18n.t('browserExtension.activityTimeline') }}
        </h3>

        <div
          class="relative ml-2 space-y-8 border-l border-gray-200 pl-4 dark:border-gray-700">
          <div
            v-for="(point, index) in groupedActivityByDay"
            :key="`${point.day}-${index}`"
            class="relative">
            <!-- Timeline Dot -->
            <div
              class="absolute -left-[21px] top-1.5 h-3 w-3 rounded-full border-2 border-white transition-colors duration-200 dark:border-gray-900"
              :class="
                index === 0 ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
              "></div>

            <div class="flex flex-col gap-3">
              <!-- Date Header -->
              <div class="flex items-center justify-between gap-2">
                <span
                  class="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {{ formatDayHeader(point.day) }}
                </span>
              </div>

              <!-- Activity Card (Clickable) -->
              <div
                class="group/card hover:shadow-sm focus-visible:outline-blue-500 cursor-pointer rounded-lg border border-gray-100 bg-gray-50 p-3 transition-all hover:border-gray-200 hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 dark:border-gray-700 dark:bg-gray-800/50 dark:hover:bg-gray-800"
                tabindex="0"
                role="button"
                @click="toggleDay(point.day)"
                @keyup.enter="toggleDay(point.day)">
                <div class="flex items-center gap-3">
                  <div
                    class="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                    <Activity class="h-4 w-4" />
                  </div>
                  <div class="flex-1">
                    <p
                      class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ point.totalSessions }}
                      {{ i18n.t('browserExtension.sessionsRecorded') }}
                    </p>
                    <p class="text-xs text-gray-500">
                      {{ displayDate(new Date(point.day)) }}
                    </p>
                  </div>
                  <!-- Chevron Indicator -->
                  <div
                    class="text-gray-400 opacity-0 transition-opacity group-hover/card:opacity-100">
                    <ChevronDown
                      :class="expandedDays[point.day] ? 'rotate-180' : ''"
                      class="h-4 w-4 transition-transform duration-200" />
                  </div>
                </div>
              </div>

              <!-- Expanded: Tool Breakdown -->
              <Transition name="expand">
                <div
                  v-if="expandedDays[point.day]"
                  class="ml-11 mt-3 space-y-2">
                  <!-- Loading state -->
                  <div
                    v-if="expandedDays[point.day]?.loading"
                    class="flex items-center gap-2 py-2">
                    <div
                      class="animate-spin border-blue-600 border-t-transparent h-4 w-4 rounded-full border-2"></div>
                    <span class="text-xs text-gray-500">{{
                      i18n.t('loading')
                    }}</span>
                  </div>

                  <!-- Tools list -->
                  <div
                    v-else-if="getDayTools(point.day).length > 0"
                    class="space-y-1.5">
                    <div
                      v-for="tool in getDayTools(point.day)"
                      :key="tool.name"
                      class="rounded-md focus-visible:outline-blue-500 flex cursor-pointer items-center justify-between bg-gray-100 px-3 py-2 transition-colors hover:bg-gray-200 focus-visible:outline-2 focus-visible:outline-offset-2 dark:bg-gray-800 dark:hover:bg-gray-700"
                      tabindex="0"
                      role="button"
                      @click="$emit('tool-click', tool.name)"
                      @keyup.enter="$emit('tool-click', tool.name)">
                      <div class="flex items-center gap-2">
                        <AppWindow class="h-4 w-4 text-gray-500" />
                        <span
                          class="text-sm font-medium text-gray-700 dark:text-gray-300"
                          >{{ tool.name }}</span
                        >
                      </div>
                      <span
                        class="text-xs font-semibold text-gray-600 dark:text-gray-400"
                        >{{ tool.sessions }}
                        {{ i18n.t('browserExtension.sessions') }}</span
                      >
                    </div>
                  </div>

                  <!-- No tools found -->
                  <div
                    v-else-if="
                      getDayTools(point.day).length === 0 &&
                      !expandedDays[point.day]?.loading
                    "
                    class="py-2">
                    <span class="text-xs text-gray-400">{{
                      i18n.t('browserExtension.noToolsData')
                    }}</span>
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import dayjs from 'dayjs'
  import relativeTime from 'dayjs/plugin/relativeTime'
  import isToday from 'dayjs/plugin/isToday'
  import isYesterday from 'dayjs/plugin/isYesterday'

  dayjs.extend(relativeTime)
  dayjs.extend(isToday)
  dayjs.extend(isYesterday)
  import { useI18n } from 'vue-i18n'
  import {
    Clock,
    Activity,
    Calendar,
    ChevronDown,
    AppWindow,
  } from 'lucide-vue-next'
  import type { DailyUsagePoint } from '@/client'
  import EmptyState from '@/components/shared/EmptyState.vue'
  import KpiCard from '../../dashboard/widgets/KpiCard.vue'
  import {
    getToolUsageStats,
    PERSON_STATS_KEY,
    type PersonStatsContext,
  } from '@/stores/browserextension/browserextension.store'
  import { displayDate } from '@/utils/formatter/date'
  import { inject, computed, ref } from 'vue'

  interface Props {
    installationId: string
    fromDate?: Date | null
    tillDate?: Date | null
  }

  const props = defineProps<Props>()
  defineEmits<{
    (e: 'tool-click', toolName: string): void
  }>()
  const i18n = useI18n()

  // Inject shared person stats from parent
  const injected = inject<PersonStatsContext>(PERSON_STATS_KEY)

  // Use injected data if available
  const personStats = computed(() => injected?.personStats.value ?? null)
  const loading = computed(() => injected?.loading.value ?? false)
  const error = computed(() => injected?.error.value ?? null)

  // Store expanded state per day and their daily tool breakdown
  const expandedDays = ref<
    Record<
      string,
      { tools: Array<{ name: string; sessions: number }>; loading: boolean }
    >
  >({})

  // Fetch daily tool breakdown for a specific day
  const fetchDayToolUsage = async (dayKey: string) => {
    if (expandedDays.value[dayKey]?.tools) return // Already loaded

    // Set loading state
    expandedDays.value[dayKey] = {
      tools: [],
      loading: true,
    }

    try {
      // Fetch usage for each top tool on this specific date
      const toolNames =
        personStats.value?.tools?.map((t) => t.software_name) || []

      // Parse dayKey (YYYY-MM-DD) strictly
      const date = dayjs(dayKey)
      const startOfDay = date.startOf('day').toDate()
      const endOfDay = date.endOf('day').toDate()

      const toolUsagePromises = toolNames.map((toolName) =>
        getToolUsageStats(props.installationId, toolName, startOfDay, endOfDay)
          .then((stats) => ({
            toolName,
            daily_usages: stats?.daily_usages || [],
          }))
          .catch((err) => ({ toolName, error: err }))
      )

      const results = await Promise.all(toolUsagePromises)

      // Process successful results
      const toolsWithSessions = results
        .filter(
          (
            result
          ): result is { toolName: string; daily_usages: DailyUsagePoint[] } =>
            !('error' in result)
        )
        .map((result) => {
          // Since we queried for this specific day range, sum all returned points
          // (API might return hourly points or just one daily point)
          const totalSessions =
            result.daily_usages.reduce((sum, point) => sum + point.count, 0) ||
            0

          return {
            name: result.toolName,
            sessions: totalSessions,
          }
        })
        .filter((t) => t.sessions > 0)
        .sort((a, b) => b.sessions - a.sessions) // Sort by most used

      expandedDays.value[dayKey] = {
        tools: toolsWithSessions,
        loading: false,
      }
    } catch (e) {
      console.error('Failed to fetch daily tool usage:', e)
      expandedDays.value[dayKey] = {
        tools: [],
        loading: false,
      }
    }
  }

  // Toggle day expansion
  const toggleDay = (dayKey: string) => {
    if (expandedDays.value[dayKey]) {
      // Collapse - remove from state
      const newState = { ...expandedDays.value }
      delete newState[dayKey]
      expandedDays.value = newState
    } else {
      // Expand - fetch and show
      fetchDayToolUsage(dayKey)
    }
  }

  // Get tool breakdown for a day
  const getDayTools = (dayKey: string) => {
    return expandedDays.value[dayKey]?.tools || []
  }

  const retryFetch = () => {
    injected?.refetch()
  }

  // Check if activity data exists
  const hasActivityData = computed(
    () =>
      personStats.value?.daily_usages &&
      personStats.value.daily_usages.length > 0
  )

  // Group daily usage by day (date in YYYY-MM-DD format)
  const groupedActivityByDay = computed(() => {
    if (!personStats.value?.daily_usages) return []

    const dayMap = new Map<
      string,
      { day: string; timeRange: string; totalSessions: number }
    >()

    personStats.value.daily_usages.forEach((point) => {
      // Use strictly YYYY-MM-DD format for keys to ensure consistency
      const dayKey = dayjs.unix(point.timestamp).format('YYYY-MM-DD')

      if (!dayMap.has(dayKey)) {
        dayMap.set(dayKey, {
          day: dayKey,
          timeRange: '',
          totalSessions: point.count,
        })
      } else {
        const existing = dayMap.get(dayKey)!
        existing.totalSessions += point.count
      }
    })

    // Convert map to array and sort by date (descending - most recent first)
    return Array.from(dayMap.values()).sort((a, b) => {
      const dateA = new Date(a.day)
      const dateB = new Date(b.day)
      return dateB.getTime() - dateA.getTime()
    })
  })

  // Format day header with better logic
  const formatDayHeader = (dayKey: string): string => {
    const date = dayjs(dayKey)

    if (date.isToday()) return i18n.t('browserExtension.today')
    if (date.isYesterday()) return i18n.t('browserExtension.yesterday')

    // For older dates, show "Friday, Jan 17, 2026"
    return date.format('dddd, MMM D, YYYY')
  }
</script>

<style scoped>
  .expand-enter-active,
  .expand-leave-active {
    transition: all 0.25s ease-out;
    overflow: hidden;
  }
  .expand-enter-from,
  .expand-leave-to {
    opacity: 0;
    max-height: 0;
    margin-top: 0;
  }
  .expand-enter-to,
  .expand-leave-from {
    opacity: 1;
    max-height: 500px;
  }
</style>
